import * as React from 'react';
import { connect } from 'react-redux';
import notify from '../../../../../shared/feedback/notifications';
import Grid from '@mui/material/Grid';
import { FeatureFlagKeys } from '../../../../../shared/featureFlags/featureFlagKeys';
import { useFeatureFlag } from '../../../../../shared/featureFlags/useFeatureFlag';
import * as actions from '../actions/ImageProcessingActions';
import { handleError } from '../../../../../shared/ErrorCatching';
import { IImageThumbnail } from '../Entities';
import { Translate } from '../../../../../shared/LanguageService';
import * as Features from '../../../../../shared/Features';
import ImageProcessingCompare from '../modal/ImageProcessingCompare';
import FormInputChangeHandler from '../../../../../shared/form/FormInputChangeHandler';
import { IComment } from '../Entities';
import MultipleReturnsCommentModel from './MultipleReturnsCommentModal';
import DraggableImageThumbnail from './DraggableImageThumbnail';
import CreateStitchedImageModal from './CreateStitchedImageModal';
import ImageUploadInProgressSkeleton from './ImageUploadInProgressSkeleton';

interface IProps {
    actions: any;
    imageType: number;
    orderRowName: string;
    orderRowId: number;
    key: number;
    numberOfPhotosInRow: any;
    shouldShowAddedPhotos: boolean;
    orderId: number;
    productProperties: any[];
    allowStitching: boolean;
    user: any;
    photoStatus: any;
    categories: any[];
    allowChangeCategories: boolean;
    imageThumbnails: any[];
    selectedImages: number[];
    selectingMultiple: boolean;
    ctrlIsDown: boolean;
    showReturnImageCommentSection: boolean;
    showCompareImages: boolean;
    displayStitchRadio?: boolean;
    showCreateStitchedImageModal?: boolean;
    leftStitchImage?: any;
    rightStitchImage?: any;
    showModalForImage: (id: number) => void;
    createStitchedImage?: (leftStitchImageId: number, rightStitchImageId: number) => void;
    imageSelected: (imageId: number, selected: boolean) => void;
    setShowStitchingModal: (showStitchingModal: boolean) => void;
    toggleReturnImageCommentSection: () => void;
    setShowCompareImages: (showCompareImages: boolean) => void;
    setShowCreateStitchedImageModal: (showCreateStitchedImageModal: boolean) => void;
    setSelectMultipleActionLoading: (selectMultipleActionLoading: boolean) => void;
    onDelete?: (photoId: number) => Promise<any>;
    canDelete?: () => boolean;
    toggleRadio?: () => void;
    leftStitchSelected?: (image: IImageThumbnail) => void;
    rightStitchSelected?: (image: IImageThumbnail) => void;
    updateCategory: (orderId: number, imageId: number, category: number) => void;
    extraSelected: number;
    editedTab: boolean;
    isReorderImagesFeatureToggleEnabled: boolean;
    imagesInProgress: any[];
}

interface ILocalComment extends IComment {
    errors: any[];
    text: string;
    toImageEditor: boolean;
    toPhotographer: boolean;
    toCustomer: boolean;
}

interface IState {
    comment: ILocalComment;
    customerSelectedImages: number[];
    extraSelected: number;
    stitching: boolean;
    toImageEditor?: boolean;
    toPhotographer?: boolean;
    draggedImageCategory: number;
}

export interface ISortOrder {
    id: number;
    idx: number;
}

enum Categories {
    Delivery = 1,
    Extra = 2,
    NoDelivery = 3
}

const ItemType = 'Thumbnail';

const DropContainer = ({ children }) => {
    const styles = {
        height: '100%',
        paddingTop: '10px'
    };

    return (
        <Grid container style={styles}>
            {children}
        </Grid>
    );
};

class ImageProcessingList extends React.Component<IProps, IState> {
    formInputChangeHandler: FormInputChangeHandler;

    constructor(props: any, context: any) {
        super(props, context);

        this.state = {
            comment: {
                text: '',
                toImageEditor: false,
                toPhotographer: false,
                toCustomer: false,
                toSe360: false,
                errors: [],
                id: -1,
                author: '',
                feature: '',
                time: null,
                imageId: null,
                show: null
            },
            customerSelectedImages: [],
            extraSelected: 0,

            stitching: false,

            draggedImageCategory: null
        };

        this.createStitchedImage = this.createStitchedImage.bind(this);
        this.canDelete = this.canDelete.bind(this);
        this.getSelectedImagesToCompare = this.getSelectedImagesToCompare.bind(this);
        this.onReturnImages = this.onReturnImages.bind(this);

        this.setCommentSectionState = this.setCommentSectionState.bind(this);
        this.formInputChangeHandler = new FormInputChangeHandler(
            this.state,
            this.setCommentSectionState
        );
        this.props.actions.updateCategory = this.props.actions.updateCategory.bind(this);
        this.getImage = this.getImage.bind(this);
    }

    canDelete = (photoId: number) => {
        return (
            this.props.canDelete() &&
            this.props.imageThumbnails.find((x) => x.id === photoId).canDelete
        );
    };

    createStitchedImage = (leftOffset: number, rightOffset: number) => {
        this.setState({ stitching: true });
        this.props.actions
            .createStitchedImage(
                this.props.leftStitchImage.id,
                leftOffset,
                this.props.rightStitchImage.id,
                rightOffset,
                this.props.orderId,
                this.props.orderRowId
            )
            .then(() => {
                notify.success(Translate('image-processing/notification/stitch-images/success'));
                this.setState({ stitching: false });
                this.props.setShowCreateStitchedImageModal(false);
                this.props.toggleRadio();
            })
            .catch((error: any) => {
                handleError(error);
                this.setState({ stitching: false });
            });
    };

    getSelectedImagesToCompare() {
        let images = [];
        this.props.selectedImages.forEach((imageId) => {
            let thumbnail = this.props.imageThumbnails.find((x) => x.id === imageId);
            images = [
                ...images,
                {
                    imageId: imageId,
                    previewReference: thumbnail.previewReference,
                    highResReference: thumbnail.highResolutionImageReference,
                    highResLoading: false
                }
            ];
        });
        return images;
    }

    enableSaveComment() {
        return this.state.comment.toSe360 || this.state.toImageEditor || this.state.toPhotographer;
    }

    setCommentSectionState(state: any) {
        this.setState({ comment: state.comment });
    }

    onReturnImages() {
        this.props.setSelectMultipleActionLoading(true);
        let comment: IComment = this.state.comment;
        let toPhotographer = comment.toPhotographer ? 1 : 0;
        let toImageEditor = comment.toImageEditor ? 2 : 0;
        let toSe360 = comment.toSe360 ? 8 : 0;
        let recipients = toPhotographer + toImageEditor + toSe360;

        this.props.actions
            .returnMultiple(this.props.orderId, this.props.selectedImages, comment.text, recipients)
            .then(() => {
                let comment = {
                    text: '',
                    toImageEditor: false,
                    toPhotographer: false,
                    toCustomer: false,
                    toSe360: false,
                    errors: [],
                    id: -1,
                    author: '',
                    feature: '',
                    time: null,
                    imageId: null,
                    show: null
                };
                this.setState({ comment: comment });
                this.formInputChangeHandler.updateObj(this.state);
                this.props.setSelectMultipleActionLoading(false);
            })
            .catch((error: any) => {
                this.props.setSelectMultipleActionLoading(false);
                handleError(error, (errors: any) => {
                    let comment = this.state.comment;
                    comment.errors = errors;
                    this.setState({ comment: comment });
                    this.formInputChangeHandler.updateObj(this.state);
                });
            })
            .then(() => {
                this.props.toggleReturnImageCommentSection();
            });
    }

    getImage(img: IImageThumbnail): IImageThumbnail {
        if (this.state.imageOverrides) {
            const imgOver = this.state.imageOverrides.find((value: IImageThumbnail) => {
                return value.id === img.id;
            });
            if (imgOver) {
                return { ...img, ...imgOver };
            }
        }
        return img;
    }

    sortThumbnailsByCategory = (thumbnailImages: IImageThumbnail[]): IImageThumbnail[] => {
        let delivery = [];
        let extra = [];
        let noDelivery = [];

        thumbnailImages.forEach((thumbnail) => {
            const img = this.getImage(thumbnail);

            const category = img.overrideProperties
                ? img.overrideProperties.categoryId
                : thumbnail.categoryId;

            if (category === Categories.Delivery) delivery.push(thumbnail);
            else if (category === Categories.Extra) extra.push(thumbnail);
            else if (category === Categories.NoDelivery) noDelivery.push(thumbnail);
        });

        const sortedThumbnails = [...delivery, ...extra, ...noDelivery];

        return sortedThumbnails;
    };

    createSortOrder = (thumbnailImages: IImageThumbnail[]): ISortOrder[] => {
        const sortOrder = [];

        thumbnailImages.forEach((thumbnail, index) => {
            sortOrder.push({
                id: thumbnail.id,
                idx: index
            });
        });

        return sortOrder;
    };

    dispatchSortOrder = (updatedThumbnails, previousThumbnails) => {
        const newSortOrder = this.createSortOrder(updatedThumbnails);
        const previousSortOrder = this.createSortOrder(previousThumbnails);

        this.props.actions.updateImagesSortOrder(
            this.props.orderId,
            newSortOrder,
            previousSortOrder
        );
    };

    handleSwap = (thumb1Id, thumb2Id) => {
        const thumb1 = this.getThumbById(thumb1Id);
        const thumb2 = this.getThumbById(thumb2Id);

        const thumb1Category = thumb1.overrideProperties
            ? thumb1.overrideProperties.categoryId
            : thumb1.categoryId;
        const thumb2Category = thumb2.overrideProperties
            ? thumb2.overrideProperties.categoryId
            : thumb2.categoryId;

        if (thumb1Category === thumb2Category) {
            const { imageThumbnails } = this.props;

            const idx1 = imageThumbnails.findIndex((thumb) => thumb1.id === thumb.id);
            const idx2 = imageThumbnails.findIndex((thumb) => thumb2.id === thumb.id);

            let updatedThumbnails = [...imageThumbnails];

            updatedThumbnails[idx1] = imageThumbnails[idx2];
            updatedThumbnails[idx2] = imageThumbnails[idx1];

            this.props.actions.updateImagesSortOrder(
                this.props.orderId,
                this.createSortOrder(updatedThumbnails),
                this.createSortOrder(imageThumbnails)
            );
        }
    };

    updateItemSortIdx = (item, dropTargetImageId, idxOffset, category) => {
        const thumb = this.getThumbById(item);

        const droppedItemCategoryId = thumb.overrideProperties
            ? thumb.overrideProperties.categoryId
            : thumb.categoryId;

        if (droppedItemCategoryId === category) {
            const { imageThumbnails } = this.props;

            const updatedThumbnails = [...imageThumbnails.filter((img) => img.id !== thumb.id)];

            const dropTargetIndex = updatedThumbnails.findIndex(
                (img) => img.id === dropTargetImageId
            );
            const insertIndex = dropTargetIndex + idxOffset;

            updatedThumbnails.splice(insertIndex, 0, thumb);

            this.dispatchSortOrder(
                this.sortThumbnailsByCategory(updatedThumbnails),
                imageThumbnails
            );
        }
    };

    handleDrop = (item, category) => {
        const thumb = this.getThumbById(item);

        const categoryId = thumb.overrideProperties
            ? thumb.overrideProperties.categoryId
            : thumb.categoryId;

        if (categoryId === category) {
            const { imageThumbnails } = this.props;
            const updatedThumbnails = imageThumbnails.filter((img) => img.id !== thumb.id);

            updatedThumbnails.push(thumb);

            this.dispatchSortOrder(
                this.sortThumbnailsByCategory(updatedThumbnails),
                imageThumbnails
            );
        }
    };

    updateCategory = (orderId, imageId, category) => {
        const { imageThumbnails } = this.props;

        this.props.actions.updateCategory(orderId, imageId, category);

        const updatedThumbnails = [...imageThumbnails].map((thumbnail) => {
            if (thumbnail.id === imageId) thumbnail.categoryId = category;
            return thumbnail;
        });

        this.dispatchSortOrder(this.sortThumbnailsByCategory(updatedThumbnails), imageThumbnails);
    };

    getThumbById = (id) => {
        return this.props.imageThumbnails.find((thumb) => thumb.id === id);
    };

    render() {
        const allowStitching = this.props.allowStitching;
        const imageThumbnails = this.props.imageThumbnails;

        imageThumbnails.sort((a, b) => {
            const sortIndexA = a.sortIndex;
            const sortIndexB = b.sortIndex;

            if (sortIndexA === 0) return -1;
            if (sortIndexB === 0) return 1;
            if (sortIndexA === null || sortIndexB === null) return 0;

            return sortIndexA - sortIndexB;
        });

        let idList = imageThumbnails.map((img: IImageThumbnail) => img.id);

        const draggable =
            this.props.isReorderImagesFeatureToggleEnabled &&
            this.props.editedTab &&
            window.innerWidth > 900; // the feature is not available now on mobile or tablet devices as long as React DND do not support touch devices

        return (
            <div>
                {this.props.imagesInProgress &&
                    this.props.imagesInProgress.map((el, index) => (
                        <ImageUploadInProgressSkeleton key={index} />
                    ))}

                <DropContainer>
                    {this.sortThumbnailsByCategory(imageThumbnails).map(
                        (img: IImageThumbnail, idx: number) => (
                            <DraggableImageThumbnail
                                key={img.id}
                                image={this.getImage(img)}
                                orderId={this.props.orderId}
                                showModalForImage={this.props.showModalForImage}
                                imageType={this.props.imageType}
                                idList={idList}
                                displayStitchRadio={
                                    allowStitching &&
                                    this.props.displayStitchRadio &&
                                    img.isCustomerApproved &&
                                    img.highResolutionHeight > img.highResolutionWidth
                                }
                                leftStitchSelected={allowStitching && this.props.leftStitchSelected}
                                rightStitchSelected={
                                    allowStitching && this.props.rightStitchSelected
                                }
                                onDelete={this.props.onDelete}
                                canDelete={this.canDelete}
                                seeNormalIcon={
                                    Features.hasFeature(
                                        Features.DownloadNormalImage,
                                        this.props.user
                                    ) ||
                                    (this.props.photoStatus.photographerIsEditor &&
                                        Features.hasFeature(
                                            Features.HandleEditedImagesWhenPhotographerIsEditor,
                                            this.props.user
                                        ))
                                }
                                seeNeedsEditingIcon={Features.hasFeature(
                                    Features.ViewNeedsEditing,
                                    this.props.user
                                )}
                                isFlex={this.props.photoStatus.hasFlex}
                                selectingMultiple={
                                    this.props.selectingMultiple || this.props.ctrlIsDown
                                }
                                imageIsSelected={this.props.selectedImages.some(
                                    (id) => id === img.id
                                )}
                                imageSelected={this.props.imageSelected}
                                ctrlIsDown={this.props.ctrlIsDown}
                                selectedExtraImages={this.props.extraSelected}
                                updateImageCategory={this.props.actions.updateCategory}
                                selectingMultipleToolBox={this.props.selectingMultiple}
                                user={this.props.user}
                                handleSwap={this.handleSwap}
                                updateItemSortIdx={this.updateItemSortIdx}
                                ItemType={ItemType}
                                lastThumb={idx === imageThumbnails.length - 1}
                                draggable={draggable}
                                canReaprove={this.props.photoStatus.canReaprove}
                                draggedImageCategory={this.state.draggedImageCategory}
                                setDraggedImageCategory={(category) =>
                                    this.setState({ draggedImageCategory: category })
                                }
                            />
                        )
                    )}
                </DropContainer>

                {this.props.showCreateStitchedImageModal && (
                    <CreateStitchedImageModal
                        leftImage={this.props.leftStitchImage}
                        rightImage={this.props.rightStitchImage}
                        isCreating={this.state.stitching}
                        create={this.createStitchedImage}
                        close={() => this.props.setShowCreateStitchedImageModal(false)}
                    />
                )}

                {this.props.showCompareImages && (
                    <ImageProcessingCompare
                        getSelectedImagesToCompare={this.getSelectedImagesToCompare}
                        onHide={() => this.props.setShowCompareImages(false)}
                    />
                )}

                {this.props.showReturnImageCommentSection && (
                    <MultipleReturnsCommentModel
                        shouldShowModal={this.props.showReturnImageCommentSection}
                        onChange={this.formInputChangeHandler.onChange}
                        onClose={this.props.toggleReturnImageCommentSection}
                        onReturnImages={this.onReturnImages}
                        errors={this.state.comment.errors}
                        comment={this.state.comment}
                    />
                )}
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            createStitchedImage: (
                leftStitchImageId: number,
                leftOffset: number,
                rightStitchImageId: number,
                rightOffset: number,
                orderId: number,
                orderRowId: number
            ) =>
                dispatch(
                    actions.createStitchedImage(
                        leftStitchImageId,
                        leftOffset,
                        rightStitchImageId,
                        rightOffset,
                        orderId,
                        orderRowId
                    )
                ),
            returnMultiple: (
                orderId: number,
                imageIds: number[],
                text: string,
                recipients: number
            ) => dispatch(actions.returnMultiple(orderId, imageIds, text, recipients)),
            updateCategory: (orderId: number, imageId: number, category: number) =>
                dispatch(actions.updateCategory(orderId, imageId, category)),
            swapCategories: (
                orderId: number,
                id1: number,
                category1: number,
                id2: number,
                category2: number
            ) => dispatch(actions.swapCategories(orderId, id1, category1, id2, category2)),
            updateImagesSortOrder: (
                orderId: number,
                sortOrder: ISortOrder[],
                previousSortOrder: ISortOrder[]
            ) => dispatch(actions.updateImagesSortOrder(orderId, sortOrder, previousSortOrder))
        }
    };
}

const ImageProcessingListWithFeatureToggle = (props) => {
    let isReorderImagesFeatureToggleEnabled = useFeatureFlag(FeatureFlagKeys.ReorderImages, false);

    return (
        <ImageProcessingList
            isReorderImagesFeatureToggleEnabled={isReorderImagesFeatureToggleEnabled}
            {...props}
        />
    );
};

function mapStateToProps(state: any) {
    return {
        user: state.user,
        photoStatus: state.imageProcessing.photoStatus,
        extraSelected: state.imageProcessing.edited.extraSelected
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageProcessingListWithFeatureToggle);
